import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTimes } from '@fortawesome/free-solid-svg-icons';
import LoadingPopup from '../../../SharedView/LoadingPopup';
import { SkeletonRows } from '../BookingDetails/SectionSkeleton';
import { DateTime } from 'luxon';
import './bookingReports.css';

const reportOptions = [
    { value: 'before_cleaning_video', label: 'Before Cleaning Video' },
    { value: 'after_cleaning_video', label: 'After Cleaning Video' },
    { value: 'stained_laundry_pickup', label: 'Stained Laundry Pickup' },
    { value: 'apartment_condition', label: 'Apartment Condition' },
    { value: 'task_report', label: 'Task Report' },
    { value: 'damages', label: 'Damages' },
];

function truncateText(str, max = 28) {
    const s = String(str || '').trim();
    if (!s) return '';
    if (s.length <= max) return s;
    return `${s.slice(0, Math.max(0, max - 3))}...`;
}

function BookingReports({ bookingId, refreshSignal }) {
    const [reports, setReports] = useState([]);
    const [error, setError] = useState(null);
    const [modalContent, setModalContent] = useState(null);
    // Viewer position within the reports list (null = viewer closed).
    const [modalIndex, setModalIndex] = useState(null);
    // Report id whose in-viewer approval just happened — shows the temporary
    // "Approved — Report Sent to Client" note before settling to "✓ Approved".
    const [viewerJustApproved, setViewerJustApproved] = useState(null);
    const viewerApproveTimer = useRef(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingReportId, setEditingReportId] = useState(null);
    const [editedText, setEditedText] = useState({ type: '', comment: '' });
    const [tasks, setTasks] = useState([]); // booking tasks for the report→task selector
    const [isProcessing, setIsProcessing] = useState(false);
    const [feedbackMessage, setFeedbackMessage] = useState('');
    const [initialLoading, setInitialLoading] = useState(true);

    // Bulk select
    const [selectedReports, setSelectedReports] = useState([]);

    // ✅ Confirm approve after video if tasks pending
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmReportId, setConfirmReportId] = useState(null);
    const [confirmPendingCount, setConfirmPendingCount] = useState(0);

    const fetchReports = useCallback(
        async (isInitial = false) => {
            if (isInitial) setInitialLoading(true);
            try {
                const response = await axios.get(`/api/booking-reports/${bookingId}`);
                if (response.status === 200 && Array.isArray(response.data)) {
                    setReports(response.data);
                } else {
                    setReports([]);
                }
            } catch (err) {
                if (err.response?.status === 404) {
                    setReports([]);
                } else {
                    console.error('Error fetching reports:', err);
                    setError('Error fetching reports.');
                }
            } finally {
                if (isInitial) setInitialLoading(false);
            }
        },
        [bookingId]
    );

    useEffect(() => {
        fetchReports(true);

        const interval = setInterval(() => {
            fetchReports(false);
        }, 15000);

        return () => clearInterval(interval);
    }, [fetchReports]);

    useEffect(() => {
        if (refreshSignal) fetchReports(false);
    }, [refreshSignal, fetchReports]);

    // Booking tasks — used to let an admin link a report to a task (which marks
    // the task completed on approval). Refetched whenever an edit form opens so
    // the selector's numbers stay in sync with the Tasks section on this page
    // (adding/deleting a task there renumbers the list).
    const fetchTasks = useCallback(() => {
        let stale = false;
        axios.get(`/api/tasks/booking/${bookingId}`)
            .then((res) => { if (!stale) setTasks(Array.isArray(res.data) ? res.data : []); })
            .catch(() => { if (!stale) setTasks([]); });
        return () => { stale = true; };
    }, [bookingId]);

    useEffect(() => fetchTasks(), [fetchTasks]);
    useEffect(() => {
        if (editingReportId != null) fetchTasks();
    }, [editingReportId, fetchTasks]);

    // Close the edit modal on Escape while it is open.
    useEffect(() => {
        if (editingReportId == null) return undefined;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') setEditingReportId(null);
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [editingReportId]);

    const taskOptions = [
        { value: null, label: '— Not linked to a task —' },
        // Numbered in creation order — same numbers the Tasks section and the client see.
        ...tasks.map((t, i) => ({ value: t.id, label: `${i + 1}. ${truncateText(t.instruction, 60) || `Task #${t.id}`}` })),
    ];

    const formatType = (type) => {
        const foundType = reportOptions.find((option) => option.value === type);
        return foundType ? foundType.label : String(type || '').replace(/_/g, ' ');
    };

    const openModal = (report) => {
        // Index-based viewer (2026-08-11, mirrors the client view): the
        // modal navigates the whole reports list instead of trapping a single
        // image, so staff can review + approve one by one without exiting.
        const idx = reports.findIndex((r) => r.id === report.id);
        setModalIndex(idx >= 0 ? idx : 0);
        setModalContent(report);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setModalContent(null);
        setModalIndex(null);
    };

    const goToReport = useCallback((idx) => {
        if (idx < 0 || idx >= reports.length) return;
        setModalIndex(idx);
        setModalContent(reports[idx]);
    }, [reports]);

    // Keyboard: ← → navigate, Escape closes — while the viewer is open.
    useEffect(() => {
        if (!isModalOpen || modalIndex == null) return undefined;
        const onKey = (e) => {
            if (e.key === 'ArrowLeft') { e.preventDefault(); goToReport(modalIndex - 1); }
            else if (e.key === 'ArrowRight') { e.preventDefault(); goToReport(modalIndex + 1); }
            else if (e.key === 'Escape') { e.preventDefault(); closeModal(); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModalOpen, modalIndex, goToReport]);

    // Approve from INSIDE the viewer: stays on the same report (2026-08-11
    // — no auto-advance) and shows the button morphing into the approved state
    // with a temporary "Report Sent to Client" note. The after-cleaning video
    // keeps its pending-tasks confirm flow.
    const approveFromViewer = async (report) => {
        // Same rule as the grid button: a report that hasn't finished
        // processing has no WhatsApp-ready media yet — approving it would
        // send the client a broken/raw file. The arrows can navigate onto
        // such a report; approving it must be impossible (2026-08-13).
        if (!report?.id || report.is_approved || !report.is_compressed) return;
        if (String(report.type) === 'after_cleaning_video') {
            await handleApproveClick(report);
            return;
        }
        try {
            // Silent approve — no "Report approved / OK" popup between items,
            // the whole point is approving in one flow. Errors still surface.
            await approveReport(report.id);
            setViewerJustApproved(report.id);
            if (viewerApproveTimer.current) clearTimeout(viewerApproveTimer.current);
            viewerApproveTimer.current = setTimeout(() => setViewerJustApproved(null), 3500);
        } catch (e) {
            console.error('viewer approve failed:', e);
            setFeedbackMessage('Failed to approve report.');
        }
    };

    useEffect(() => () => {
        if (viewerApproveTimer.current) clearTimeout(viewerApproveTimer.current);
    }, []);

    const handleEdit = (report) => {
        if (editingReportId === report.id) {
            setEditingReportId(null);
        } else {
            setEditingReportId(report.id);
            setEditedText({
                type: report.type || '',
                comment: report.comment || '',
                is_approved: report.is_approved || false,
                task_id: report.task_id ?? null,
            });
        }
    };

    // Linking a report to a task only completes it when it's a "task report",
    // so auto-set the type when a task is chosen (admin can still change it).
    const setEditTask = (taskId) => {
        setEditedText((prev) => ({
            ...prev,
            task_id: taskId,
            type: taskId != null && prev.type !== 'task_report' ? 'task_report' : prev.type,
        }));
    };

    const handleSave = async (reportId) => {
        setIsProcessing(true);
        setFeedbackMessage('Saving report...');
        try {
            const res = await axios.put(`/api/booking-reports/${reportId}`, editedText);
            setEditingReportId(null);
            setFeedbackMessage(res.data?.task_completed
                ? 'Report saved — linked task marked completed.'
                : 'Report updated successfully.');
            // Refetch so the task link/instruction and completion reflect.
            await fetchReports(false);
        } catch (err) {
            console.error('Error updating report:', err);
            setFeedbackMessage(err.response?.data?.error || 'Failed to update report.');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDelete = async (reportId) => {
        if (!window.confirm('Are you sure you want to delete this report? This action cannot be undone.')) return;

        setIsProcessing(true);
        setFeedbackMessage('Deleting report...');
        try {
            await axios.delete(`/api/booking-reports/${reportId}`);
            setReports((prev) => prev.filter((r) => r.id !== reportId));
            setSelectedReports((prev) => prev.filter((id) => id !== reportId));
            setFeedbackMessage('Report deleted successfully.');
        } catch (err) {
            console.error('Error deleting report:', err);
            setFeedbackMessage('Failed to delete report.');
        } finally {
            setIsProcessing(false);
        }
    };

    const approveReport = async (reportId) => {
        await axios.post(`/api/booking-reports/${reportId}/approve`);
        setReports((prev) =>
            prev.map((r) => (r.id === reportId ? { ...r, is_approved: true } : r))
        );
    };

    const handleQuickApprove = async (reportId) => {
        setIsProcessing(true);
        setFeedbackMessage('Approving report...');
        try {
            await approveReport(reportId);
            setFeedbackMessage('Report approved.');
        } catch (err) {
            console.error('Approval failed:', err);
            setFeedbackMessage('Failed to approve report.');
        } finally {
            setIsProcessing(false);
        }
    };

    // ✅ Before approving AFTER video: check pending tasks and confirm
    const handleApproveClick = async (report) => {
        if (!report?.id) return;

        // normal path for all non-after video
        if (String(report.type) !== 'after_cleaning_video') {
            await handleQuickApprove(report.id);
            return;
        }

        // after video: check tasks first
        setIsProcessing(true);
        setFeedbackMessage('Checking pending tasks...');
        try {
            const res = await axios.get(`/api/tasks/booking/${bookingId}`);
            const allTasks = Array.isArray(res.data) ? res.data : [];
            const pendingTasks = allTasks.filter(
                (t) => String(t.status || '').toLowerCase() === 'pending'
            );

            if (pendingTasks.length > 0) {
                setConfirmPendingCount(pendingTasks.length);
                setConfirmReportId(report.id);
                setConfirmOpen(true);
                setFeedbackMessage('');
                return;
            }

            // no pending tasks -> approve directly
            await approveReport(report.id);
            setFeedbackMessage('Report approved.');
        } catch (err) {
            console.error('Failed to check pending tasks:', err);
            setFeedbackMessage('Failed to check pending tasks.');
        } finally {
            setIsProcessing(false);
        }
    };

    const confirmApproveAfterVideo = async () => {
        if (!confirmReportId) return;
        setConfirmOpen(false);

        // now do the real approve
        await handleQuickApprove(confirmReportId);

        // cleanup
        setConfirmReportId(null);
        setConfirmPendingCount(0);
    };

    const cancelApproveAfterVideo = () => {
        setConfirmOpen(false);
        setConfirmReportId(null);
        setConfirmPendingCount(0);
    };

    const toggleSelectReport = (id) => {
        setSelectedReports((prev) => (prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]));
    };

    const handleDeleteSelected = async () => {
        if (selectedReports.length === 0) return;
        if (!window.confirm(`Delete ${selectedReports.length} selected reports?`)) return;

        setIsProcessing(true);
        setFeedbackMessage('Deleting selected reports...');
        try {
            await axios.post('/api/booking-reports/bulk-delete', { ids: selectedReports });
            setReports((prev) => prev.filter((r) => !selectedReports.includes(r.id)));
            setSelectedReports([]);
            setFeedbackMessage('Selected reports deleted.');
        } catch (err) {
            console.error('Bulk delete failed:', err);
            setFeedbackMessage('Failed to delete selected reports.');
        } finally {
            setIsProcessing(false);
        }
    };

    if (error) return <div>{error}</div>;

    const editingReport =
        editingReportId != null ? reports.find((r) => r.id === editingReportId) : null;
    const editingIsVideo = editingReport
        ? /\.(mp4|mov|avi|mkv)$/i.test(editingReport.file_url)
        : false;
    const editingFileName = editingReport
        ? String(editingReport.file_url || '').split('?')[0].split('/').pop() || ''
        : '';

    return (
        <div className="booking-reports-container">
            {selectedReports.length > 0 && (
                <button className="report-bulk-delete-button" onClick={handleDeleteSelected}>
                    Delete Selected ({selectedReports.length})
                </button>
            )}

            {initialLoading ? (
                <SkeletonRows rows={2} />
            ) : reports.length === 0 ? (
                <div className="no-booking-reports">No reports for this booking yet.</div>
            ) : (
                <div className="booking-reports-main-part">
                    <p className="booking-30-days">
                        Please note attachments are only available for 60 days from date of upload.
                    </p>

                    <div className="booking-reports-grid">
                        {reports.map((report) => {
                            const isVideo = /\.(mp4|mov|avi|mkv)$/i.test(report.file_url);
                            const isTaskReport = String(report.type) === 'task_report';

                            // ✅ this is what you actually want under "Task Report"
                            const taskPreviewSource =
                                report.task_instruction ||
                                report.instruction ||          // just in case you named it differently somewhere
                                report.comment ||              // fallback
                                '';

                            const shortTaskText = truncateText(taskPreviewSource, 34);

                            return (
                                <div
                                    key={report.id}
                                    className={`booking-report-card${report.is_compressed ? ' booking-report-card--openable' : ''}`}
                                    // The whole card opens the viewer (2026-08-11),
                                    // not just the media itself; controls inside stop
                                    // propagation so they keep their own actions.
                                    onClick={() => report.is_compressed && openModal(report)}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedReports.includes(report.id)}
                                        onChange={() => toggleSelectReport(report.id)}
                                        onClick={(e) => e.stopPropagation()}
                                        className="report-select-checkbox"
                                        disabled={!report.is_compressed}
                                    />

                                    <div className="booking-report-thumbnail">
                                        {!report.is_compressed ? (
                                            <div className="report-processing-placeholder">
                                                <div className="spinner" />
                                                <p>Processing...</p>
                                            </div>
                                        ) : isVideo ? (
                                            <div className="report-thumbnail-wrapper">
                                                <img
                                                    src={report.thumbnail_url}
                                                    alt="Video Thumbnail"
                                                    className="booking-report-image-preview"
                                                />
                                                <div className="play-button-overlay">&#9658;</div>
                                            </div>
                                        ) : (
                                            <img
                                                src={report.file_url}
                                                alt="Report Attachment"
                                                className="booking-report-image-preview"
                                            />
                                        )}
                                    </div>

                                    <div className="booking-report-header">
                                        <div>
                                            <p className="booking-report-type">{formatType(report.type)}</p>

                                            {/* ✅ Task Report short version under the report type */}
                                            {isTaskReport && (
                                                <p
                                                    style={{
                                                        margin: '4px 0 0 0',
                                                        fontSize: 12,
                                                        color: '#6B7280',
                                                        lineHeight: 1.25,
                                                        maxWidth: 260,
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                    }}
                                                    title={String(taskPreviewSource || '')}
                                                >
                                                    {shortTaskText || '—'}
                                                </p>
                                            )}

                                        </div>

                                        {report.is_compressed && (
                                            <FontAwesomeIcon
                                                icon={faPen}
                                                className="report-edit-icon"
                                                onClick={(e) => { e.stopPropagation(); handleEdit(report); }}
                                            />
                                        )}
                                    </div>

                                    <p className="booking-report-date">
                                        <strong>
                                            {DateTime.fromISO(report.uploaded_at, { zone: 'utc' })
                                                .setZone('Asia/Dubai')
                                                .toFormat('dd LLLL yyyy, HH:mm')}
                                        </strong>
                                    </p>

                                    <p className="booking-report-comment">
                                        <strong>Comment:</strong> {report.comment || 'No comment'}
                                    </p>


                                    <p className="booking-report-comment">
                                        <strong>Approved:</strong>{' '}
                                        <span className={report.is_approved ? 'approved-text' : 'not-approved-text'}>
                                            {report.is_approved ? ' Yes' : ' No'}
                                        </span>

                                        {!report.is_approved && report.is_compressed && (
                                            <button
                                                className="report-approve-button"
                                                onClick={(e) => { e.stopPropagation(); handleApproveClick(report); }}
                                            >
                                                Approve
                                            </button>
                                        )}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Edit report modal */}
            {editingReport && (
                <div
                    className="report-modal-overlay"
                    onMouseDown={() => setEditingReportId(null)}
                >
                    <div
                        className="report-modal-panel"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Edit report"
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <div className="report-modal-header">
                            <div>
                                <h3 className="report-modal-title">Edit report</h3>
                                <p className="report-modal-subtitle">
                                    Uploaded{' '}
                                    {DateTime.fromISO(editingReport.uploaded_at, { zone: 'utc' })
                                        .setZone('Asia/Dubai')
                                        .toFormat('dd LLLL yyyy, HH:mm')}
                                </p>
                            </div>
                            <button
                                type="button"
                                className="report-modal-close"
                                aria-label="Close"
                                onClick={() => setEditingReportId(null)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <div className="report-modal-body">
                            <div className="report-modal-preview">
                                <div className="report-modal-thumb">
                                    <img
                                        src={editingReport.thumbnail_url || editingReport.file_url}
                                        alt="Report preview"
                                    />
                                    {editingIsVideo && (
                                        <span className="report-modal-thumb-play">&#9658;</span>
                                    )}
                                </div>
                                <div className="report-modal-preview-meta">
                                    <span className="report-modal-preview-type">
                                        {editingIsVideo ? 'Video attachment' : 'Photo attachment'}
                                    </span>
                                    <span className="report-modal-preview-file" title={editingFileName}>
                                        {editingFileName || 'Attached file'}
                                    </span>
                                </div>
                            </div>

                            <div className="report-modal-field">
                                <label className="report-modal-label">Report type</label>
                                <Select
                                    className="report-edit-select"
                                    options={reportOptions}
                                    value={reportOptions.find((option) => option.value === editedText.type)}
                                    onChange={(selectedOption) =>
                                        setEditedText((prev) => ({
                                            ...prev,
                                            type: selectedOption.value,
                                            // A task link only makes sense on a task report.
                                            task_id: selectedOption.value === 'task_report' ? prev.task_id : null,
                                        }))
                                    }
                                    isSearchable={false}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    menuPlacement="auto"
                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 100000 }) }}
                                />
                            </div>

                            {editedText.type === 'task_report' && (
                                <div className="report-modal-field">
                                    <label className="report-modal-label">Linked task</label>
                                    <Select
                                        className="report-edit-taskselect"
                                        options={taskOptions}
                                        value={taskOptions.find((o) => o.value === (editedText.task_id ?? null))}
                                        onChange={(opt) => setEditTask(opt ? opt.value : null)}
                                        placeholder="Link to a task…"
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        menuPlacement="auto"
                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 100000 }) }}
                                    />
                                    {editedText.task_id != null && (
                                        <div className="report-modal-taskhint">
                                            Approving this report will mark the linked task completed.
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="report-modal-field">
                                <label className="report-modal-label">Comment</label>
                                <textarea
                                    className="report-modal-textarea"
                                    placeholder="Add a comment…"
                                    value={editedText.comment}
                                    onChange={(e) => setEditedText({ ...editedText, comment: e.target.value })}
                                />
                            </div>

                            <div className="report-modal-toggle-row">
                                <div className="report-modal-toggle-text">
                                    <span className="report-modal-toggle-title">Approved</span>
                                    <span className="report-modal-toggle-desc">
                                        Approved reports are visible to the client
                                    </span>
                                </div>
                                <label className="report-modal-switch">
                                    <input
                                        type="checkbox"
                                        checked={!!editedText.is_approved}
                                        onChange={(e) =>
                                            setEditedText({ ...editedText, is_approved: e.target.checked })
                                        }
                                    />
                                    <span className="report-modal-switch-slider" />
                                </label>
                            </div>
                        </div>

                        <div className="report-modal-footer">
                            <button
                                type="button"
                                className="report-modal-delete"
                                onClick={() => handleDelete(editingReport.id)}
                            >
                                Delete
                            </button>
                            <div className="report-modal-footer-actions">
                                <button
                                    type="button"
                                    className="report-modal-cancel"
                                    onClick={() => setEditingReportId(null)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="report-modal-save"
                                    onClick={() => handleSave(editingReport.id)}
                                >
                                    Save changes
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ✅ Confirm modal for approving after cleaning video when pending tasks exist */}
            {confirmOpen && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.45)',
                        zIndex: 99999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 16,
                    }}
                    onClick={cancelApproveAfterVideo}
                >
                    <div
                        style={{
                            width: 'min(560px, 100%)',
                            background: '#fff',
                            borderRadius: 14,
                            padding: 18,
                            boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                            <h3 style={{ margin: 0, fontSize: 16, color: '#111827' }}>Confirm Approval</h3>
                            <button
                                type="button"
                                onClick={cancelApproveAfterVideo}
                                style={{
                                    border: '1px solid #E5E7EB',
                                    background: '#fff',
                                    borderRadius: 10,
                                    padding: '8px 10px',
                                    cursor: 'pointer',
                                    color: '#374151',
                                }}
                            >
                                Close
                            </button>
                        </div>

                        <div style={{ marginTop: 12, fontSize: 14, color: '#111827', lineHeight: 1.35 }}>
                            There are still <strong>{confirmPendingCount}</strong> pending task(s) for this booking.
                            <br />
                            Are you sure you want to approve the <strong>After Cleaning Video</strong> and mark the booking completed?
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
                            <button
                                type="button"
                                onClick={cancelApproveAfterVideo}
                                style={{
                                    border: '1px solid #E5E7EB',
                                    background: '#fff',
                                    borderRadius: 10,
                                    padding: '10px 14px',
                                    cursor: 'pointer',
                                    color: '#374151',
                                    fontWeight: 600,
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={confirmApproveAfterVideo}
                                style={{
                                    border: '1px solid #FF5A60',
                                    background: '#FF5A60',
                                    borderRadius: 10,
                                    padding: '10px 14px',
                                    cursor: 'pointer',
                                    color: '#fff',
                                    fontWeight: 700,
                                }}
                            >
                                Yes, Approve
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Full-Size Media Modal */}
            {isModalOpen && modalContent && (() => {
                // Always render the LIVE row from state — after an in-viewer
                // approve the reports array updates and the button must flip.
                const current = (modalIndex != null && reports[modalIndex]) ? reports[modalIndex] : modalContent;
                const hasPrev = modalIndex != null && modalIndex > 0;
                const hasNext = modalIndex != null && modalIndex + 1 < reports.length;
                return (
                <div
                    className="booking-report-modal"
                    onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
                >
                    {reports.length > 1 && (
                        <div className="booking-report-counter">
                            {(modalIndex ?? 0) + 1} / {reports.length}
                        </div>
                    )}

                    {hasPrev && (
                        <button
                            type="button"
                            className="booking-report-nav booking-report-nav--prev"
                            onClick={() => goToReport(modalIndex - 1)}
                            aria-label="Previous report"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <polyline points="15 6 9 12 15 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    )}

                    <div className="booking-report-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button type="button" className="modal-x" onClick={closeModal}>&times;</button>

                        {!current.is_compressed ? (
                            <div className="booking-report-viewer-processing">
                                <div className="spinner" />
                                <p>Processing…</p>
                                <p className="booking-report-viewer-processing-note">
                                    This report is still being prepared — it can be
                                    viewed and approved once processing finishes.
                                </p>
                            </div>
                        ) : /\.(mp4|mov|avi|mkv)$/i.test(current.file_url) ? (
                            <video key={current.id} controls className="booking-report-full-video" autoPlay>
                                <source src={current.file_url} type="video/mp4" />
                            </video>
                        ) : (
                            <img
                                key={current.id}
                                src={current.file_url}
                                alt="Full Report Attachment"
                                className="booking-report-full-image"
                            />
                        )}

                        <div className="booking-report-modal-info">
                            <p>
                                <strong>
                                    {DateTime.fromISO(current.uploaded_at, { zone: 'utc' })
                                        .setZone('Asia/Dubai')
                                        .toFormat('dd LLLL yyyy, HH:mm')}
                                </strong>
                            </p>
                            <p>
                                <strong>Report Type:</strong> {formatType(current.type)}
                            </p>

                            {/* ✅ Task reports show LONG version here (modal) */}
                            {String(current.type) === 'task_report' ? (
                                <p>
                                    <strong>Task Instruction:</strong>{' '}
                                    {current.task_instruction || current.comment || 'N/A'}
                                </p>
                            ) : (
                                ''
                            )}
                            <p>
                                <strong>Comment:</strong> {current.comment || 'N/A'}
                            </p>

                            {/* Approve without leaving the viewer (2026-08-11):
                                same action as the grid button; on success the
                                button animates into the approved state and
                                briefly notes the report is now sent to the
                                client, then settles to a plain "✓ Approved". */}
                            {current.is_approved ? (
                                <div
                                    key={viewerJustApproved === current.id ? 'flash' : 'done'}
                                    className={`booking-report-viewer-approved${viewerJustApproved === current.id ? ' booking-report-viewer-approved--flash' : ''}`}
                                >
                                    ✓ Approved{viewerJustApproved === current.id ? ' — Report Sent to Client' : ''}
                                </div>
                            ) : !current.is_compressed ? null : (
                                <button
                                    type="button"
                                    className="booking-report-viewer-approve"
                                    disabled={isProcessing}
                                    onClick={() => approveFromViewer(current)}
                                >
                                    {isProcessing ? 'Working…' : 'Approve'}
                                </button>
                            )}
                        </div>
                    </div>

                    {hasNext && (
                        <button
                            type="button"
                            className="booking-report-nav booking-report-nav--next"
                            onClick={() => goToReport(modalIndex + 1)}
                            aria-label="Next report"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <polyline points="9 6 15 12 9 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    )}
                </div>
                );
            })()}

            <LoadingPopup
                isLoading={isProcessing}
                feedbackMessage={feedbackMessage}
                onClose={() => setFeedbackMessage('')}
            />
        </div>
    );
}

export default BookingReports;
